import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Microphone, Stop, SignOut, SquaresFour, Sparkle,
  ShieldCheck, ChatCircle, WaveSine, Notebook, Plus, Trash, CaretRight,
} from "@phosphor-icons/react";

const FONT_SERIF = "'Cormorant Garamond', serif";
const FONT_SANS = "'Manrope', system-ui, sans-serif";

// same palette as /app/design_guidelines.json (mirrors Chat.jsx)
const C = {
  appBg: "#F9F8F6",
  centerBg: "#FFFFFF",
  sidebarBg: "#F3F1EC",
  userBubble: "#F3F1EC",
  textPrimary: "#1C241C",
  textSecondary: "#5C665A",
  accent: "#9E8155",
  borderSubtle: "#E5E1D8",
};

export default function VoiceChat() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [bootstrap, setBootstrap] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);       // { role: 'user'|'brand', text }
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("Tap the mic and speak");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);
  const scrollRef = useRef(null);

  // ---- bootstrap on mount (loads this surface's own session list — voice
  // chat history is scoped separately from text chat and the live-call surface)
  useEffect(() => {
    api.get("/relationship/bootstrap", { params: { channel: "voice" } })
      .then(({ data }) => setBootstrap(data))
      .catch(() => toast.error("Couldn't load your profile"));
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, busy]);

  const sessions = bootstrap?.sessions || [];

  const sessionGroups = useMemo(() => {
    const groups = {};
    for (const s of sessions) {
      const d = new Date(s.updated_at || s.created_at);
      const key = isToday(d) ? "Today" : isYesterday(d) ? "Yesterday" : d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
      (groups[key] ||= []).push(s);
    }
    return Object.entries(groups);
  }, [sessions]);

  const refreshBootstrap = async () => {
    try {
      const { data } = await api.get("/relationship/bootstrap", { params: { channel: "voice" } });
      setBootstrap(data);
    } catch {
      // non-fatal — sidebar just won't refresh
    }
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/relationship/sessions/${sid}`);
      const events = data.events || [];
      const loaded = [];
      for (const e of events) {
        loaded.push({ role: "user", text: e.query, ts: e.created_at });
        loaded.push({
          role: "brand",
          text: e.answer,
          followup: e.followup || "",
          trust: { total_tokens: e.tokens, cost_inr: e.cost_inr },
          created_at: e.created_at,
        });
      }
      setTurns(loaded);
      setSessionId(sid);
    } catch {
      toast.error("Couldn't open that conversation");
    }
  };

  const newSession = () => {
    setTurns([]);
    setSessionId(null);
    setStatus("Tap the mic and speak");
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm("End this conversation? It will be removed from your history.")) return;
    try {
      await api.delete(`/relationship/sessions/${sid}`);
      if (sessionId === sid) newSession();
      await refreshBootstrap();
      toast.success("Conversation removed");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  const onLogout = () => { logout(); nav("/"); };

  const startRecording = async () => {
    if (busy || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        sendVoice(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setStatus("Listening…");
    } catch {
      toast.error("Microphone access is required for voice chat");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const sendVoice = async (blob) => {
    setBusy(true);
    setStatus("Thinking…");
    try {
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      if (sessionId) form.append("session_id", sessionId);

      const { data } = await api.post("/chat/voice", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSessionId(data.session_id);
      setTurns((t) => [
        ...t,
        { role: "user", text: data.transcript },
        { role: "brand", text: data.answer, followup: data.followup || "", trust: data.trust || {}, created_at: data.created_at },
      ]);

      if (data.audio_base64) {
        const audioSrc = `data:audio/${data.audio_format || "wav"};base64,${data.audio_base64}`;
        if (audioElRef.current) {
          audioElRef.current.src = audioSrc;
          await audioElRef.current.play().catch(() => {});
        }
        setStatus("Speaking…");
      } else {
        setStatus("Tap the mic and speak");
      }
      refreshBootstrap();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice chat hit a snag — please try again.");
      setStatus("Tap the mic and speak");
    } finally {
      setBusy(false);
    }
  };

  const onAudioEnded = () => setStatus("Tap the mic and speak");

  return (
    <div
      className="h-screen w-screen grid overflow-hidden"
      style={{
        gridTemplateColumns: "290px 1fr",
        background: C.appBg,
        fontFamily: FONT_SANS,
        color: C.textPrimary,
      }}
      data-testid="voice-chat-layout-wrapper"
    >
      {/* ================= LEFT: identity / nav sidebar ================= */}
      <aside
        className="h-full flex flex-col overflow-hidden"
        style={{ background: C.sidebarBg, borderRight: `1px solid ${C.borderSubtle}` }}
        data-testid="voice-chat-sidebar-left"
      >
        <div className="px-6 pt-7 pb-4">
          <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
            <Sparkle size={14} weight="fill" />
            <span className="text-[11px] tracking-[0.18em] uppercase font-medium">ITC Concierge</span>
          </div>
          <div style={{ fontFamily: FONT_SERIF, fontSize: 26, lineHeight: 1.1, color: C.textPrimary }} className="tracking-tight">
            Voice conversation
          </div>
        </div>

        <div className="px-4 pb-3 space-y-2">
          <Link
            to="/chat"
            data-testid="voice-back-to-text-chat"
            className="w-full flex items-center gap-2 px-4 py-3 transition-all"
            style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 10, color: C.textPrimary }}
          >
            <ChatCircle size={14} /> Switch to text chat
          </Link>
          <Link
            to="/voice-live"
            data-testid="voice-try-live-call"
            className="w-full flex items-center gap-2 px-4 py-3 transition-all"
            style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 10, color: C.textPrimary }}
          >
            <WaveSine size={14} /> Try live call (beta)
          </Link>
          <button
            onClick={newSession}
            data-testid="voice-new-session-btn"
            className="w-full flex items-center justify-between px-4 py-3 transition-all"
            style={{
              background: "#FFFFFF",
              border: `1px solid ${C.borderSubtle}`,
              borderRadius: 10,
              color: C.textPrimary,
            }}
          >
            <span className="flex items-center gap-2 text-[14px]">
              <Plus size={14} /> New conversation
            </span>
            <CaretRight size={12} style={{ color: C.accent }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-4" data-testid="voice-session-list">
          {sessionGroups.length === 0 ? (
            <div className="px-4 py-8 text-sm italic" style={{ color: C.textSecondary }}>
              Your conversations will live here.
            </div>
          ) : (
            sessionGroups.map(([label, items]) => (
              <div key={label} className="mb-4">
                <div className="px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-medium" style={{ color: C.textSecondary }}>
                  {label}
                </div>
                {items.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className={`group px-4 py-3 mb-0.5 cursor-pointer transition-all flex items-start gap-2 ${sessionId === s.id ? "" : "hover:bg-white"}`}
                    style={{
                      borderRadius: 8,
                      background: sessionId === s.id ? "#FFFFFF" : "transparent",
                      border: `1px solid ${sessionId === s.id ? C.borderSubtle : "transparent"}`,
                    }}
                    data-testid={`voice-session-item-${s.id}`}
                  >
                    <Notebook size={14} style={{ color: C.accent, marginTop: 3 }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] truncate" style={{ color: C.textPrimary }}>
                        {s.title || "Untitled conversation"}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: C.textSecondary }}>
                        {s.turn_count || 0} {s.turn_count === 1 ? "exchange" : "exchanges"}
                        {s.topic_tags?.[0] ? ` · ${s.topic_tags[0]}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`voice-session-delete-${s.id}`}
                      title="Remove"
                    >
                      <Trash size={12} style={{ color: C.textSecondary }} />
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-4" style={{ borderTop: `1px solid ${C.borderSubtle}` }}>
          <div className="text-[11px] tracking-[0.12em] uppercase mb-1" style={{ color: C.textSecondary }}>
            Signed in as
          </div>
          <div className="text-[14px] truncate" style={{ color: C.textPrimary }}>
            {user?.email}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="voice-back-admin-btn"
                className="text-[12px] flex items-center gap-1 px-2 py-1"
                style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 6, color: C.textPrimary }}
              >
                <SquaresFour size={12} /> Admin
              </Link>
            )}
            <button
              onClick={onLogout}
              data-testid="voice-logout-btn"
              className="text-[12px] flex items-center gap-1 px-2 py-1"
              style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 6, color: C.textPrimary }}
            >
              <SignOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* ================= CENTER: voice conversation ================= */}
      <section
        className="h-full flex flex-col overflow-hidden"
        style={{ background: C.centerBg }}
        data-testid="voice-chat-center-area"
      >
        <header
          className="px-10 py-6 flex items-start justify-between gap-6"
          style={{ borderBottom: `1px solid ${C.borderSubtle}` }}
          data-testid="voice-chat-header"
        >
          <div>
            <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
              <ShieldCheck size={12} weight="fill" />
              <span className="text-[11px] tracking-[0.18em] uppercase font-medium">Relationship Desk · Voice</span>
            </div>
            <div style={{ fontFamily: FONT_SERIF, fontSize: 28, lineHeight: 1.1, color: C.textPrimary }} className="tracking-tight">
              Speak, and I'll answer aloud.
            </div>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="voice-chat-message-list">
          <div className="max-w-[720px] mx-auto px-10 py-12">
            {turns.length === 0 && (
              <div data-testid="voice-chat-empty" className="text-[15px]" style={{ color: C.textSecondary }}>
                Tap the microphone below and ask your question out loud — I'll transcribe it, think it through, and reply in voice.
              </div>
            )}
            <div className="space-y-8">
              {turns.map((t, i) => (
                <div key={i}>
                  {t.role === "user" ? (
                    <div className="flex justify-end" data-testid={`voice-user-msg-${i}`}>
                      <div
                        className="px-5 py-3 max-w-[85%] text-[15px] leading-relaxed"
                        style={{ background: C.userBubble, color: C.textPrimary, borderRadius: 16 }}
                      >
                        {t.text}
                      </div>
                    </div>
                  ) : (
                    <div data-testid={`voice-brand-msg-${i}`}>
                      <div
                        style={{ fontFamily: FONT_SERIF, fontSize: 20, lineHeight: 1.55, color: C.textPrimary }}
                        className="whitespace-pre-wrap"
                      >
                        {t.text}
                      </div>
                      {t.followup && (
                        <div
                          className="mt-5 pl-4 flex items-start gap-2"
                          style={{
                            borderLeft: `2px solid ${C.accent}`,
                            color: C.textSecondary,
                            opacity: 0.95,
                          }}
                          data-testid={`voice-followup-${i}`}
                        >
                          <Sparkle size={12} weight="fill" style={{ color: C.accent, marginTop: 5, flexShrink: 0 }} />
                          <span
                            style={{
                              fontFamily: FONT_SANS,
                              fontSize: 13,
                              fontStyle: "italic",
                              lineHeight: 1.5,
                              letterSpacing: "0.01em",
                            }}
                          >
                            {t.followup}
                          </span>
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-3">
                        <span
                          className="text-[11px] font-mono px-2 py-0.5"
                          style={{
                            color: C.accent,
                            background: C.userBubble,
                            border: `1px solid ${C.borderSubtle}`,
                            borderRadius: 999,
                          }}
                          data-testid={`voice-turn-cost-${i}`}
                          title={`${t.trust?.total_tokens || 0} tokens`}
                        >
                          ₹{Number(t.trust?.cost_inr || 0).toFixed(4)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* mic control */}
        <div className="px-10 pb-10 pt-4 flex flex-col items-center gap-3" style={{ background: C.centerBg }}>
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={busy}
            data-testid={recording ? "voice-stop-btn" : "voice-start-btn"}
            className="w-20 h-20 flex items-center justify-center transition-all disabled:opacity-40"
            style={{
              borderRadius: "50%",
              background: recording ? C.accent : C.textPrimary,
              color: "#FFFFFF",
              boxShadow: recording ? `0 0 0 8px ${C.userBubble}` : "none",
            }}
            title={recording ? "Stop and send" : "Speak your question"}
          >
            {recording ? <Stop size={28} weight="fill" /> : <Microphone size={28} weight="fill" />}
          </button>
          <div className="flex items-center gap-1.5 text-[13px]" style={{ color: C.textSecondary }} data-testid="voice-status">
            <WaveSine size={14} />
            {status}
          </div>
        </div>
      </section>

      <audio ref={audioElRef} onEnded={onAudioEnded} data-testid="voice-audio-player" />
    </div>
  );
}

// helpers
function isToday(d) {
  const n = new Date();
  return d.toDateString() === n.toDateString();
}
function isYesterday(d) {
  const n = new Date();
  n.setDate(n.getDate() - 1);
  return d.toDateString() === n.toDateString();
}
